import { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";

import { PlusIcon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { tabletUp } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import { Message } from "simplydo/react/ui/message";
import { Modal } from "simplydo/react/ui/modal";

import api from "api";
import { useAppSelector } from "store";
import { useDidMount } from "utils/useDidMount";
import util from "utils/utils";

import SmallIdeaCard from "components/lib/SmallIdeaCard";

import type { ChallengeDetail, ChallengeIdeaListItem } from "../types";

const UserIdeasContainer = styled.div`
  display: flex;
  ${tabletUp} {
    justify-content: center;
    align-items: center;
  }
`;

const UserIdeasBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 20px;
`;

const NewIdeaButton = styled(Button)`
  margin: 10px;
`;

type NewIdeaProps = { redirectToLogin: (organisationCode?: string) => void };

const NewIdea = ({ redirectToLogin }: NewIdeaProps) => {
  const user = useAppSelector((state) => state.user);

  const [failed, setFailed] = useState(false);
  const [creatingIdea, setCreatingIdea] = useState(false);
  const [userIdeas, setUserIdeas] = useState<ChallengeIdeaListItem[]>([]);
  const [challenge, setChallenge] = useState<ChallengeDetail | null>(null);
  const [userIdeasModalOpen, setUserIdeasModalOpen] = useState(false);
  const hasIdea = userIdeas.length > 0;
  const { t } = useTranslation();

  const params = useParams();
  const challengeId = params.id;
  const navigate = useNavigate();

  const createIdea = useCallback(
    (currentChallenge: ChallengeDetail) => {
      window.scrollTo(0, 0);
      setCreatingIdea(true);
      util.newIdea(currentChallenge, user, (idea) => {
        setCreatingIdea(false);
        navigate(`/ideas/${idea._id}`);
      });
    },
    [user, navigate],
  );

  useDidMount(() => {
    if (!challengeId) return;
    setCreatingIdea(true);
    api.challenges.get(
      challengeId,
      (newChallenge) => {
        setChallenge(newChallenge);
        const canMakeIdea = util.canCreateIdea(user, newChallenge);
        if (user && canMakeIdea) {
          api.challenges.getIdeas(
            challengeId,
            { scope: "me" },
            ({ ideas }) => {
              setUserIdeas(ideas);
              if (ideas.length > 0) {
                setCreatingIdea(false);
              } else {
                createIdea(newChallenge);
              }
            },
            () => setFailed(true),
          );
        } else if (!user) {
          setFailed(true);
          window.scrollTo(0, 0);
          redirectToLogin(newChallenge?.ownerOrganisation?.code);
        } else {
          setFailed(true);
          window.scrollTo(0, 0);
        }
      },
      () => setFailed(true),
    );
  });

  useEffect(() => {
    if (hasIdea) setUserIdeasModalOpen(true);
  }, [hasIdea]);

  return (
    <div>
      {failed ? (
        <Message color="orange">
          <H3>
            We were unable to create a new {t("generic.idea")} for you
            <Subheader>
              The settings for this {t("generic.challenge")} may be preventing
              you from creating new {t("generic.ideas")}. For support with this
              please get in touch with the owner of the {t("generic.challenge")}
              .
            </Subheader>
          </H3>
        </Message>
      ) : null}
      {creatingIdea && !failed ? <Loader /> : null}
      <Modal
        size="small"
        open={userIdeasModalOpen}
        onClose={() => setUserIdeasModalOpen(false)}
      >
        <UserIdeasContainer>
          <UserIdeasBox>
            <H3>Continue an existing idea...</H3>
            {userIdeas.map((idea, index) =>
              idea?._id ? (
                <SmallIdeaCard key={idea._id} idea={idea} index={index} />
              ) : null,
            )}
            {challenge ? (
              <NewIdeaButton
                onClick={() => createIdea(challenge)}
                style={{ margin: "10px 15px", width: "100%" }}
                color="primary"
              >
                <Icon icon={PlusIcon} />
                Create new idea
              </NewIdeaButton>
            ) : null}
            {challenge ? (
              <Link to={`/challenges/${challenge._id}`}>Back to challenge</Link>
            ) : null}
          </UserIdeasBox>
        </UserIdeasContainer>
      </Modal>
    </div>
  );
};

export default NewIdea;
